import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Trash2, Check, ExternalLink, Search, X, CheckCheck, ChevronRight, Reply, Loader2, Settings } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

export default function AdminContactPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | unread
  const [opened, setOpened] = useState(null);   // currently viewed message
  const [markingAll, setMarkingAll] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [routes, setRoutes] = useState([]); // configured subject→email routes (for origin tabs)
  const [tab, setTab] = useState('all');     // all | __interpreters__ | <route label_es> | __otros__

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/contact');
      setRows(data || []);
    } catch { setRows([]); }
    try {
      const { data } = await api.get('/admin/contact/config');
      setRoutes(data?.contact_routes || []);
    } catch { /* keep empty */ }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 3000);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  async function markRead(id) {
    try {
      await api.post(`/admin/contact/${id}/mark-read`);
      setRows((prev) => prev.map((r) => r.id === id ? { ...r, read: true } : r));
    } catch (err) { setFeedback({ type: 'error', message: formatApiError(err) }); }
  }

  async function markAllRead() {
    if (!window.confirm('¿Marcar todos los mensajes como leídos?')) return;
    setMarkingAll(true);
    try {
      const { data } = await api.post('/admin/contact/mark-all-read');
      setRows((prev) => prev.map((r) => ({ ...r, read: true })));
      setFeedback({ type: 'success', message: `${data.updated || 0} mensaje${(data.updated || 0) === 1 ? '' : 's'} marcado${(data.updated || 0) === 1 ? '' : 's'} como leído${(data.updated || 0) === 1 ? '' : 's'}.` });
    } catch (err) { setFeedback({ type: 'error', message: formatApiError(err) }); }
    finally { setMarkingAll(false); }
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar este mensaje?')) return;
    try {
      await api.delete(`/admin/contact/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      if (opened?.id === id) setOpened(null);
    } catch (err) { setFeedback({ type: 'error', message: formatApiError(err) }); }
  }

  function openMessage(row) {
    setOpened(row);
    if (!row.read) markRead(row.id); // auto-mark on open
  }

  function categoryOf(r) {
    if (r.interpreter_slug) return '__interpreters__';
    const subj = (r.subject || '').trim().toLowerCase();
    for (const rt of routes) {
      const labels = [(rt.label_es || '').trim().toLowerCase(), (rt.label_en || '').trim().toLowerCase()];
      if (subj && labels.includes(subj)) return rt.label_es || rt.label_en;
    }
    return '__otros__';
  }

  const filtered = rows.filter((r) => {
    if (filter === 'unread' && r.read) return false;
    if (tab !== 'all' && categoryOf(r) !== tab) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [r.name, r.email, r.subject, r.message].some((v) => (v || '').toLowerCase().includes(q));
  });
  const unreadCount = rows.filter((r) => !r.read).length;

  // Build origin tabs: Todos · Intérpretes · <each configured subject> · Otros.
  const countFor = (key) => rows.filter((r) => categoryOf(r) === key).length;
  const unreadFor = (key) => rows.filter((r) => categoryOf(r) === key && !r.read).length;
  const seenLabels = new Set();
  const routeTabs = [];
  for (const rt of routes) {
    const label = rt.label_es || rt.label_en;
    if (!label || seenLabels.has(label)) continue;
    seenLabels.add(label);
    routeTabs.push({ key: label, label, count: countFor(label), unread: unreadFor(label) });
  }
  const tabs = [
    { key: 'all', label: 'Todos', count: rows.length, unread: unreadCount },
    { key: '__interpreters__', label: 'Intérpretes', count: countFor('__interpreters__'), unread: unreadFor('__interpreters__') },
    ...routeTabs,
    { key: '__otros__', label: 'Otros', count: countFor('__otros__'), unread: unreadFor('__otros__') },
  ];

  return (
    <div data-testid="admin-contact-page" className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2"><Mail size={22} />Mensajes de contacto</h1>
          <p className="text-charcoal-500 text-sm mt-1">{rows.length} mensajes · <span data-testid="contact-unread-count">{unreadCount}</span> sin leer</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/admin/contact/editor"
            data-testid="contact-config-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-charcoal-600 border border-gray-200 hover:border-cmic-300 transition-colors"
            title="Editar el formulario de contacto y a dónde se envía cada mensaje"
          >
            <Settings size={13} /> Editar formulario
          </Link>
          <button
            onClick={() => setFilter('all')}
            data-testid="contact-filter-all"
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === 'all' ? 'bg-cmic-500 text-white' : 'bg-white text-charcoal-600 border border-gray-200 hover:border-cmic-300'}`}
          >Todos</button>
          <button
            onClick={() => setFilter('unread')}
            data-testid="contact-filter-unread"
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === 'unread' ? 'bg-cmic-500 text-white' : 'bg-white text-charcoal-600 border border-gray-200 hover:border-cmic-300'}`}
          >Sin leer</button>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              disabled={markingAll}
              data-testid="contact-mark-all-read-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-60 transition-colors"
              title="Marcar todos los mensajes como leídos"
            >
              {markingAll ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} />}
              Leer todos
            </button>
          )}
        </div>
      </header>

      {feedback && (
        <div className={`px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {feedback.message}
        </div>
      )}

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, email o asunto…"
          data-testid="contact-search"
          className="w-full bg-white rounded-xl border border-gray-200 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
        />
      </div>

      {/* Origin tabs: Todos · Intérpretes · <asuntos configurados> · Otros */}
      <div className="flex items-center gap-1.5 flex-wrap border-b border-gray-200 pb-2" data-testid="contact-origin-tabs">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            data-testid={`contact-tab-${tb.key}`}
            title={`${tb.count} mensaje${tb.count === 1 ? '' : 's'}${tb.unread ? ` · ${tb.unread} sin leer` : ''}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              tab === tb.key ? 'bg-cmic-500 text-white' : 'bg-white text-charcoal-600 border border-gray-200 hover:border-cmic-300'
            }`}
          >
            {tb.label}
            {tb.unread > 0 ? (
              <span
                data-testid={`contact-tab-${tb.key}-unread`}
                className={`min-w-[18px] h-[16px] px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${tab === tb.key ? 'bg-white text-cmic-600' : 'bg-rose-500 text-white'}`}
              >
                {tb.unread}
              </span>
            ) : (
              <span className={`min-w-[18px] h-[16px] px-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${tab === tb.key ? 'bg-white/25 text-white' : 'bg-gray-100 text-charcoal-500'}`}>
                {tb.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center text-charcoal-500">
          <Mail size={32} className="mx-auto mb-3 text-charcoal-300" />
          {filter === 'unread' ? 'No hay mensajes sin leer.' : 'No hay mensajes aún.'}
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => openMessage(r)}
                data-testid={`contact-row-${r.id}`}
                className={`w-full text-left bg-white rounded-xl border p-4 hover:shadow-md hover:border-cmic-300 transition-all flex items-start gap-3 ${r.read ? 'border-gray-200' : 'border-cmic-300 shadow-sm bg-cmic-50/30'}`}
              >
                {/* Unread dot */}
                <div className="pt-1.5 flex-shrink-0">
                  <span className={`block w-2.5 h-2.5 rounded-full ${r.read ? 'bg-transparent' : 'bg-cmic-500'}`} aria-hidden />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`font-semibold truncate ${r.read ? 'text-charcoal-700' : 'text-charcoal-900'}`}>{r.name || 'Sin nombre'}</span>
                    <span className="text-xs text-charcoal-400">·</span>
                    <span className="text-xs text-charcoal-500 truncate">{r.email}</span>
                    {r.interpreter_slug && (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${r.routed_to === 'interpreter' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                        data-testid={`contact-row-interpreter-${r.id}`}
                        title={r.routed_to === 'interpreter' ? `Enviado directamente a ${r.interpreter_name || r.interpreter_slug}` : 'El intérprete no tiene email registrado; se envió a CMIC'}
                      >
                        {r.routed_to === 'interpreter' ? '→ Intérprete' : '→ CMIC (sin email)'}
                      </span>
                    )}
                  </div>
                  <div className={`text-sm truncate ${r.read ? 'text-charcoal-500' : 'text-charcoal-800 font-medium'}`}>
                    {r.subject || <span className="italic text-charcoal-400">(sin asunto)</span>}
                  </div>
                  <p className="text-xs text-charcoal-500 truncate mt-1">{(r.message || '').slice(0, 180)}</p>
                </div>
                <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                  <span className="text-[10px] text-charcoal-400 whitespace-nowrap">{r.created_at ? formatRelativeMx(r.created_at) : ''}</span>
                  <ChevronRight size={14} className="text-charcoal-300" />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Detail modal */}
      {opened && (
        <MessageDetailModal
          message={opened}
          onClose={() => setOpened(null)}
          onDelete={() => remove(opened.id)}
        />
      )}
    </div>
  );
}

function MessageDetailModal({ message, onClose, onDelete }) {
  const mailto = message.email
    ? `mailto:${encodeURIComponent(message.email)}?subject=${encodeURIComponent('Re: ' + (message.subject || 'Tu mensaje al CMIC'))}`
    : null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="contact-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 h-14 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Mail size={18} className="text-cmic-600 flex-shrink-0" />
            <h2 className="font-semibold text-charcoal-800 truncate">{message.subject || 'Mensaje sin asunto'}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100" data-testid="contact-detail-close">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center gap-3 text-sm flex-shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-charcoal-400 font-semibold">De</div>
            <div className="font-medium text-charcoal-800">{message.name || 'Sin nombre'}</div>
          </div>
          {message.email && (
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-charcoal-400 font-semibold">Email</div>
              <a href={`mailto:${message.email}`} className="text-cmic-600 hover:text-cmic-700 inline-flex items-center gap-1 truncate">
                {message.email} <ExternalLink size={10} className="flex-shrink-0" />
              </a>
            </div>
          )}
          <div className="ml-auto text-right">
            <div className="text-[11px] uppercase tracking-wider text-charcoal-400 font-semibold">Recibido</div>
            <div className="text-xs text-charcoal-600">{message.created_at ? new Date(message.created_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</div>
          </div>
        </div>
        <div className="px-5 py-5 overflow-y-auto flex-1" data-testid="contact-detail-body">
          <p className="text-sm text-charcoal-700 whitespace-pre-wrap leading-relaxed">{message.message}</p>
          {message.interpreter_slug && (
            <div
              className={`mt-5 rounded-xl border p-4 ${message.routed_to === 'interpreter' ? 'bg-emerald-50/60 border-emerald-200' : 'bg-amber-50/60 border-amber-200'}`}
              data-testid="contact-detail-routing"
            >
              <p className="text-[11px] uppercase tracking-wider font-bold mb-1.5 text-charcoal-600">
                Solicitud directa de intérprete
              </p>
              <p className="text-sm text-charcoal-800">
                <strong>{message.interpreter_name || message.interpreter_slug}</strong>
                <span className="text-charcoal-400 ml-1">({message.interpreter_slug})</span>
              </p>
              {message.delivery ? (
                <p className="text-xs text-charcoal-600 mt-2">
                  {message.routed_to === 'interpreter' ? (
                    <>El correo se envió directamente a <strong className="text-charcoal-800">{message.delivery.to_email}</strong>{message.delivery.ok ? '' : ' (⚠️ falló el envío vía Brevo)'}.</>
                  ) : (
                    <>El intérprete no tiene email registrado; el correo se envió al buzón institucional <strong className="text-charcoal-800">{message.delivery.to_email}</strong>.</>
                  )}
                </p>
              ) : (
                <p className="text-xs text-charcoal-500 mt-2 italic">Envío en cola…</p>
              )}
            </div>
          )}
        </div>
        <div className="px-5 h-14 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
          <button onClick={onDelete} className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 font-medium" data-testid="contact-detail-delete">
            <Trash2 size={12} /> Eliminar
          </button>
          {mailto && (
            <a
              href={mailto}
              className="inline-flex items-center gap-1.5 bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-1.5 rounded-lg text-sm font-semibold shadow-sm"
              data-testid="contact-detail-reply"
            >
              <Reply size={14} /> Responder por email
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function formatRelativeMx(iso) {
  try {
    const d = new Date(iso);
    const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `${diffMin}m`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH}h`;
    const diffD = Math.round(diffH / 24);
    if (diffD < 7) return `${diffD}d`;
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

